import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import { SEOHead } from "@/components/SEOHead";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PACKAGES_OPTIONS = [
  "Starter",
  "Business",
  "Premium",
  "Noch nicht sicher",
];

export default function KontaktPage() {
  const location = useLocation();
  const formRef = useRef(null);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    message: "",
    budget: "",
    package_interest: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Pre-select package via ?paket=… and scroll to form on hash #demo-form or paket param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paket = (params.get("paket") || "").toLowerCase();
    const match = PACKAGES_OPTIONS.find((p) => p.toLowerCase() === paket);
    if (match) {
      setForm((f) => ({ ...f, package_interest: match }));
    }
    if (location.hash === "#demo-form" || paket) {
      // Wait one tick so the form is rendered before scrolling
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [location.search, location.hash]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) {
      setError("Bitte geben Sie Ihren Firmennamen an.");
      return;
    }
    if (!form.contact_name.trim()) {
      setError("Bitte geben Sie Ihren Namen an.");
      return;
    }
    if (!form.contact_email.trim()) {
      setError("Bitte geben Sie Ihre E-Mail-Adresse an.");
      return;
    }
    if (!form.contact_phone.trim()) {
      setError("Bitte geben Sie Ihre Telefonnummer an.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        company_name: form.company_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        message: `${form.package_interest ? `Paket-Interesse: ${form.package_interest}\n\n` : ""}${form.message}`,
        budget: form.budget,
      };
      await axios.post(`${API_URL}/api/public/leads`, payload);
      setSubmitted(true);
    } catch (err) {
      setError("Fehler beim Senden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <SEOHead
        title="Kostenlose Demo anfragen"
        description="Jetzt kostenlos Demo anfragen — wir erstellen Ihre individuelle Website-Demo in 24 Stunden. Unverbindlich und ohne versteckte Kosten."
        canonical="/kontakt"
      />
      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-4">Kontakt</p>
            <h1
              className="font-black text-5xl sm:text-6xl text-white mb-5 leading-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Lassen Sie uns sprechen.
            </h1>
            <p className="text-xl text-white/50 leading-relaxed">
              Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden mit Ihrer individuellen Demo-Website.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section id="demo-form" ref={formRef} className="px-6 pb-24 scroll-mt-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-12">
          {/* Side info */}
          <div className="space-y-6">
            <div className="bg-[#0A1220] border border-white/5 rounded-2xl p-6">
              <h3 className="font-bold text-white mb-3">Was Sie erwartet</h3>
              <ul className="space-y-3">
                {[
                  "Rückmeldung innerhalb von 24h",
                  "Individuelle Demo-Website kostenfrei",
                  "Kein Verkaufsdruck",
                  "Persönliches Gespräch auf Wunsch",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-white/60">
                    <CheckCircle2 size={14} className="text-cyan-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-cyan-400/10 to-cyan-400/0 border border-cyan-400/20 rounded-2xl p-6">
              <p className="text-xs uppercase tracking-widest text-cyan-400 font-semibold mb-2">So läuft's ab</p>
              <ol className="space-y-3 text-sm text-white/70">
                <li className="flex gap-3">
                  <span className="w-5 h-5 rounded-full bg-cyan-400/15 text-cyan-400 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                  <span>Du füllst das Formular aus</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-5 h-5 rounded-full bg-cyan-400/15 text-cyan-400 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                  <span>Wir bauen dir eine kostenlose Demo-Website</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-5 h-5 rounded-full bg-cyan-400/15 text-cyan-400 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
                  <span>Du entscheidest in Ruhe – ohne Verpflichtung</span>
                </li>
              </ol>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="bg-[#0A1220] border border-green-400/20 rounded-2xl p-10 text-center" data-testid="contact-success">
                <div className="w-16 h-16 rounded-full bg-green-400/10 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 size={30} className="text-green-400" />
                </div>
                <h2
                  className="font-black text-3xl text-white mb-3"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  Anfrage erhalten!
                </h2>
                <p className="text-white/55 mb-6 max-w-md mx-auto">
                  Vielen Dank, <strong className="text-white">{form.contact_name || form.company_name}</strong>! Wir melden uns innerhalb von 24 Stunden mit Ihrer individuellen Demo-Website.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 text-white font-medium rounded-xl hover:bg-white/10 transition-all border border-white/10"
                >
                  Zurück zur Startseite
                </Link>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-[#0A1220] border border-white/5 rounded-2xl p-8"
                data-testid="contact-form"
              >
                <h3
                  className="font-bold text-xl text-white mb-6"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  Demo-Website anfragen
                </h3>

                {error && (
                  <div className="mb-5 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm" data-testid="contact-error">
                    {error}
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-white/30 mb-2">
                      Firmenname *
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={form.company_name}
                      onChange={handleChange}
                      placeholder="Musterfirma GmbH"
                      required
                      data-testid="contact-company-input"
                      className="w-full bg-white/5 border border-white/8 rounded-xl px-4 py-3 text-white placeholder-white/25 text-sm focus:outline-none focus:border-cyan-400/50 focus:bg-white/8 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-white/30 mb-2">
                      Ihr Name *
                    </label>
                    <input
                      type="text"
                      name="contact_name"
                      value={form.contact_name}
                      onChange={handleChange}
                      placeholder="Max Mustermann"
                      required
                      data-testid="contact-name-input"
                      className="w-full bg-white/5 border border-white/8 rounded-xl px-4 py-3 text-white placeholder-white/25 text-sm focus:outline-none focus:border-cyan-400/50 focus:bg-white/8 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-white/30 mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      name="contact_email"
                      value={form.contact_email}
                      onChange={handleChange}
                      placeholder="max@musterfirma.de"
                      required
                      data-testid="contact-email-input"
                      className="w-full bg-white/5 border border-white/8 rounded-xl px-4 py-3 text-white placeholder-white/25 text-sm focus:outline-none focus:border-cyan-400/50 focus:bg-white/8 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-white/30 mb-2">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      name="contact_phone"
                      value={form.contact_phone}
                      onChange={handleChange}
                      placeholder="+49 123 456789"
                      required
                      data-testid="contact-phone-input"
                      className="w-full bg-white/5 border border-white/8 rounded-xl px-4 py-3 text-white placeholder-white/25 text-sm focus:outline-none focus:border-cyan-400/50 focus:bg-white/8 transition-all"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-white/30 mb-2">
                    Interessiert an welchem Paket?
                  </label>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {PACKAGES_OPTIONS.map((pkg) => (
                      <button
                        key={pkg}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, package_interest: pkg }))}
                        className={`px-4 py-3 rounded-xl text-sm font-medium text-left transition-all border ${
                          form.package_interest === pkg
                            ? "bg-cyan-400/15 border-cyan-400/40 text-cyan-400"
                            : "bg-white/3 border-white/5 text-white/50 hover:border-white/15 hover:text-white/70"
                        }`}
                      >
                        {pkg}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-white/30 mb-2">
                    Ihre Nachricht (optional)
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Beschreiben Sie kurz Ihr Unternehmen, Ihre aktuelle Website-Situation und was Sie sich von einer neuen Website erhoffen..."
                    rows={4}
                    data-testid="contact-message-input"
                    className="w-full bg-white/5 border border-white/8 rounded-xl px-4 py-3 text-white placeholder-white/25 text-sm focus:outline-none focus:border-cyan-400/50 focus:bg-white/8 transition-all resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="w-full flex items-center justify-center gap-3 py-4 bg-cyan-400 text-slate-900 font-bold rounded-xl hover:bg-cyan-300 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-xl shadow-cyan-400/20 text-base"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      Demo-Anfrage absenden
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <p className="text-xs text-white/25 text-center mt-4">
                  Mit dem Absenden stimmen Sie unserer Datenschutzerklärung zu.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
